import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Loader from "../Loader/Loader";

const REASON_MAX = 500;

const money = (n, currency) =>
  `${currency ? currency + " " : ""}${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

/* Shared "Cancel invoice" confirmation, used for booking invoices (Dashboard,
   booking page) and Branch Jobs invoices.

   An invoice can cover several bookings / jobs, and cancelling it cancels it for
   all of them. The dialog says so explicitly and lists the ones the user did NOT
   select, so nothing is cancelled by surprise.

   Props
     show, onHide
     loadImpacts()    -> Promise<Impact[]>   one per invoice behind the selection
       Impact = { id, number, currency, items: [{ key, label, detail, amount, paid }], hasPayments }
     selectedKeys     Set of item keys the user actually selected (to flag the others)
     noun             "booking" | "job"  (singular; drives the wording)
     onConfirm(reason, impacts) -> Promise   performs the cancel; throw to keep the dialog open
*/
function InvoiceCancelDialog({ show, onHide, loadImpacts, selectedKeys, noun = "booking", onConfirm }) {
  const [impacts, setImpacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setImpacts([]);
    setReason("");
    setLoadError("");
    setSubmitError("");
    setLoading(true);
    Promise.resolve()
      .then(() => loadImpacts())
      .then((list) => {
        if (!cancelled) setImpacts(list || []);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || "Could not load the invoice details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // loadImpacts is a fresh closure each render; only (re)load when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const plural = (n) => `${noun}${n === 1 ? "" : "s"}`;
  const selected = selectedKeys || new Set();
  const others = impacts.flatMap((imp) => imp.items.filter((it) => !selected.has(it.key)));
  const totalItems = impacts.reduce((n, imp) => n + imp.items.length, 0);
  const hasPayments = impacts.some((imp) => imp.hasPayments);
  const canSubmit = !loading && !busy && !loadError && impacts.length > 0 && reason.trim().length > 0;

  const submit = async () => {
    setBusy(true);
    setSubmitError("");
    try {
      await onConfirm(reason.trim(), impacts);
    } catch (err) {
      setSubmitError(err?.message || "Could not cancel the invoice");
    } finally {
      setBusy(false);
    }
  };

  const numbers = impacts.map((i) => i.number).join(", ");

  return (
    <Modal show={show} onHide={() => !busy && onHide()} centered backdrop="static">
      <Modal.Header closeButton={!busy}>
        <Modal.Title style={{ fontSize: "18px" }}>
          Cancel invoice{impacts.length > 1 ? "s" : ""}
          {numbers ? ` ${numbers}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="d-flex justify-content-center py-4">
            <Loader style={{ width: 60, height: 30 }} />
          </div>
        )}

        {loadError && <div className="alert alert-danger py-2">{loadError}</div>}

        {!loading && !loadError && impacts.length > 0 && (
          <>
            {others.length > 0 && (
              <div className="alert alert-warning py-2" role="alert" style={{ fontSize: "14px" }}>
                <strong>
                  {others.length} other {plural(others.length)} {others.length === 1 ? "is" : "are"} on{" "}
                  {impacts.length > 1 ? "these invoices" : "this invoice"} and will also be cancelled.
                </strong>{" "}
                An invoice is cancelled as a whole - all {totalItems} {plural(totalItems)} below
                {others.length ? " (including the ones you did not select)" : ""} become uninvoiced and can be
                invoiced again.
              </div>
            )}
            {others.length === 0 && (
              <p style={{ fontSize: "14px", color: "#374151" }}>
                The invoice is kept on record as <strong>cancelled</strong> and its number is not reused. The{" "}
                {plural(totalItems)} below can be invoiced again.
              </p>
            )}

            {hasPayments && (
              <div className="alert alert-info py-2" role="alert" style={{ fontSize: "13px" }}>
                Some {plural(2)} already have a payment recorded. Cancelling the invoice does <strong>not</strong>{" "}
                reverse any payment.
              </div>
            )}

            {impacts.map((imp) => (
              <div key={imp.id} className="mb-3">
                {impacts.length > 1 && (
                  <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: 4 }}>{imp.number}</div>
                )}
                <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
                  {imp.items.map((it) => {
                    const isSelected = selected.has(it.key);
                    return (
                      <div
                        key={it.key}
                        className="d-flex justify-content-between align-items-start px-3 py-2"
                        style={{
                          fontSize: "13px",
                          borderBottom: "1px solid #f3f4f6",
                          background: isSelected ? "#fff" : "#fffbeb"
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {it.label}
                            {!isSelected && (
                              <span
                                className="ms-2"
                                style={{
                                  fontSize: "11px",
                                  background: "#fde68a",
                                  borderRadius: 10,
                                  padding: "1px 8px"
                                }}
                              >
                                also cancelled
                              </span>
                            )}
                          </div>
                          {it.detail && <div style={{ color: "#6b7280" }}>{it.detail}</div>}
                        </div>
                        <div className="text-end" style={{ whiteSpace: "nowrap" }}>
                          <div>{money(it.amount, imp.currency)}</div>
                          {Number(it.paid || 0) > 0 && (
                            <div style={{ color: "#6b7280", fontSize: "12px" }}>paid {money(it.paid, imp.currency)}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <label htmlFor="invoice-cancel-reason" className="form-label" style={{ fontSize: "14px", fontWeight: 500 }}>
              Reason for cancelling <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <textarea
              id="invoice-cancel-reason"
              className="form-control"
              rows={3}
              maxLength={REASON_MAX}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. wrong customer, amount corrected, duplicate invoice"
              disabled={busy}
            />
            <div className="text-end" style={{ fontSize: "12px", color: "#6b7280" }}>
              {reason.length}/{REASON_MAX}
            </div>
          </>
        )}

        {submitError && <div className="alert alert-danger py-2 mt-2 mb-0">{submitError}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-outline-secondary px-4" onClick={onHide} disabled={busy}>
          Keep invoice
        </button>
        <button className="btn btn-danger px-4" onClick={submit} disabled={!canSubmit}>
          {busy ? <Loader style={{ width: 40, height: 20 }} /> : "Cancel invoice"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default InvoiceCancelDialog;
